import { ref } from '#imports'
import { cloneDeep } from 'lodash-es'
import { defineStore } from 'pinia'
import { DEFAULT_SETTINGS, useDYAPersonalSetting, type DYAPersonalSetting, type DYFavoriteMenu } from '../composables/dya-personal-setting'
import type { DYAMenuItem } from '../components/DYAMenu.vue'

export const useDYAdminMainStore = defineStore('nuxt3-module-admin-doyac-main', () => {
  const menu = ref<DYAMenuItem[]>([])
  /** 좌측 사이드바 메뉴 검색어. 값이 있으면 DYAMenu가 트리 대신 검색 결과 목록을 보여준다. */
  const menuSearchKeyword = ref('')
  const personalSetting = ref<DYAPersonalSetting>(DEFAULT_SETTINGS)
  const pageHelpMemoNavSettings = ref<{
    isMainOpened: boolean
    isSectionOpened: boolean
    pageUrl?: string
    componentId?: string
    sectionId?: string
    hasPageMemos?: boolean
  }>({
    isMainOpened: false,
    isSectionOpened: false
  })

  const personalSettingApi = useDYAPersonalSetting()

  const loadPersonalSetting = async () => {
    personalSetting.value = await personalSettingApi.get()
  }

  const addFavoriteMenu = async (menu: DYFavoriteMenu) => {
    if (personalSetting.value) {
      personalSetting.value.favoriteMenu.push(menu)
      // await syncPersonalSetting()
    }
  }

  const removeFavoriteMenu = async (menu: DYFavoriteMenu) => {
    if (personalSetting.value) {
      const index = personalSetting.value.favoriteMenu.findIndex(entry => entry.to === menu.to)
      personalSetting.value.favoriteMenu.splice(index, 1)
      // await syncPersonalSetting()
    }
  }

  const syncPersonalSetting = async () => {
    // 스토어의 값을 직접 파라미터로 사용하면 스토어 상태 변화에 따라 네트워크를 매번 다시 요청하므로
    // 반드시 클론해서 사용해야함.
    await personalSettingApi.save(cloneDeep(personalSetting.value))
  }

  // 메뉴 트리 전체에서 특정 id를 가진 항목이 있는지 탐색
  const _hasMenuId = (from: DYAMenuItem[], id: string): boolean => {
    for (const item of from) {
      if (item.id === id) return true
      if (item.children && _hasMenuId(item.children, id)) return true
    }
    return false
  }

  const _addMenu = (from: DYAMenuItem[], id: string, menuItem: DYAMenuItem, at: 'prepend' | 'append') => {
    for (let i = 0; i < from.length; i++) {
      if (from[i].id === id) { // 아이디가 일치하면 멈추고 여기서 추가 작업 진행
        from.splice(at === 'prepend' ? i : i + 1, 0, menuItem)
        return true
      }

      if (from[i].children) { // 아이디가 같지는 않지만 자식이 있다면 자식 트리 탐색
        if (_addMenu(from[i].children!, id, menuItem, at)) {
          return true
        }
      }
    }

    return false
  }

  const addMenu = (id: string, menuItem: DYAMenuItem, at: 'prepend' | 'append' = 'append') => {
    // 이미 같은 id의 메뉴가 존재하면 중복 추가 방지
    if (menuItem.id && _hasMenuId(menu.value, menuItem.id)) return
    _addMenu(menu.value, id, menuItem, at)
  }

  const openPageHelpMemoNav = (type: 'main' | 'section', componentId?: string, sectionId?: string) => {
    if (type === 'main') {
      pageHelpMemoNavSettings.value.isSectionOpened = false
      pageHelpMemoNavSettings.value.isMainOpened = !pageHelpMemoNavSettings.value.isMainOpened
    } else {
      pageHelpMemoNavSettings.value.isMainOpened = false
      pageHelpMemoNavSettings.value.isSectionOpened = true

      pageHelpMemoNavSettings.value.componentId = componentId
      pageHelpMemoNavSettings.value.sectionId = sectionId
    }
  }

  const resetPageHelpMemoNavSettings = () => {
    pageHelpMemoNavSettings.value.isMainOpened = false
    pageHelpMemoNavSettings.value.isSectionOpened = false
    pageHelpMemoNavSettings.value.pageUrl = undefined
    pageHelpMemoNavSettings.value.componentId = undefined
    pageHelpMemoNavSettings.value.sectionId = undefined
    pageHelpMemoNavSettings.value.hasPageMemos = false
  }

  return {
    menu,
    menuSearchKeyword,
    personalSetting,
    pageHelpMemoNavSettings,
    addMenu,
    addFavoriteMenu,
    removeFavoriteMenu,
    loadPersonalSetting,
    syncPersonalSetting,
    openPageHelpMemoNav,
    resetPageHelpMemoNavSettings
  }
})